import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Alert, Form } from 'react-bootstrap';
import { FaChevronLeft } from 'react-icons/fa';
import Meta from '../components/Meta';
import { setCredentials } from '../slices/authSlice';
import { useProfileMutation } from '../slices/usersApiSlice';

const MIN_PASSWORD_LENGTH = 8;

const errorText = (err) =>
    err?.data?.errors?.[0]?.message || err?.data?.message || 'Something went wrong. Please try again.';

const Feedback = ({ result }) => result && (
    <Alert variant={result.ok ? 'success' : 'danger'} className="py-2" role={result.ok ? 'status' : 'alert'}>
        {result.text}
    </Alert>
);

const AccountDetailsScreen = () => {
    const dispatch = useDispatch();
    const { userInfo } = useSelector((state) => state.auth);
    const [updateProfile, { isLoading }] = useProfileMutation();

    const [name, setName] = useState(userInfo?.name || '');
    const [nameResult, setNameResult] = useState(null);

    const [email, setEmail] = useState('');
    const [emailPassword, setEmailPassword] = useState('');
    const [emailResult, setEmailResult] = useState(null);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordResult, setPasswordResult] = useState(null);

    const save = async (body, setResult, successText) => {
        setResult(null);
        try {
            const res = await updateProfile(body).unwrap();
            dispatch(setCredentials(res));
            setResult({ ok: true, text: successText(res) });
            return true;
        } catch (err) {
            setResult({ ok: false, text: errorText(err) });
            return false;
        }
    };

    const saveName = (e) => {
        e.preventDefault();
        save({ name: name.trim() }, setNameResult, () => 'Your name has been updated.');
    };

    const saveEmail = async (e) => {
        e.preventDefault();
        const saved = await save(
            { email: email.trim(), currentPassword: emailPassword },
            setEmailResult,
            (res) => `We've sent a confirmation link to ${res.pendingEmail}. Your email stays the same until you click it.`
        );
        if (saved) {
            setEmail('');
            setEmailPassword('');
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setPasswordResult({ ok: false, text: `Your new password must be at least ${MIN_PASSWORD_LENGTH} characters.` });
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordResult({ ok: false, text: `Your new passwords don't match.` });
            return;
        }
        const saved = await save(
            { password: newPassword, currentPassword },
            setPasswordResult,
            () => 'Your password has been changed.'
        );
        if (saved) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        }
    };

    return (
        <div className="account-page">
            <Meta title="Account Details" />
            <Link to="/profile" className="account-back"><FaChevronLeft aria-hidden="true" /> My Account</Link>
            <h1 className="account-title">Account Details</h1>

            <section className="account-section">
                <h2>Your name</h2>
                <Feedback result={nameResult} />
                <Form onSubmit={saveName}>
                    <Form.Group controlId="account-name" className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={50} />
                    </Form.Group>
                    <button type="submit" className="wishlist-action" disabled={isLoading || name.trim() === userInfo?.name}>Save Name</button>
                </Form>
            </section>

            <section className="account-section">
                <h2>Email address</h2>
                <p className="account-section__current">Currently <strong>{userInfo?.email}</strong></p>
                <Feedback result={emailResult} />
                <Form onSubmit={saveEmail}>
                    <Form.Group controlId="account-email" className="mb-3">
                        <Form.Label>New email address</Form.Label>
                        <Form.Control type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </Form.Group>
                    <Form.Group controlId="account-email-password" className="mb-3">
                        <Form.Label>Current password</Form.Label>
                        <Form.Control type="password" autoComplete="current-password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} required />
                    </Form.Group>
                    <button type="submit" className="wishlist-action" disabled={isLoading}>Change Email</button>
                </Form>
            </section>

            <section className="account-section">
                <h2>Password</h2>
                <Feedback result={passwordResult} />
                <Form onSubmit={savePassword}>
                    <Form.Group controlId="account-current-password" className="mb-3">
                        <Form.Label>Current password</Form.Label>
                        <Form.Control type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
                    </Form.Group>
                    <Form.Group controlId="account-new-password" className="mb-3">
                        <Form.Label>New password</Form.Label>
                        <Form.Control type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                        <Form.Text>At least {MIN_PASSWORD_LENGTH} characters.</Form.Text>
                    </Form.Group>
                    <Form.Group controlId="account-confirm-password" className="mb-3">
                        <Form.Label>Confirm new password</Form.Label>
                        <Form.Control type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                    </Form.Group>
                    <button type="submit" className="wishlist-action" disabled={isLoading}>Change Password</button>
                </Form>
            </section>

            <p className="account-help">
                Forgotten your password, or signed up with Google?{' '}
                <Link to="/passwordreset">Set a new one by email</Link>. You'll need one to change your email address.
            </p>
        </div>
    );
};

export default AccountDetailsScreen;
